/**
 * `/app/arena?view=open` lands the Arena on the Open Board (Home's "Find a rival", specs/home-page AC-29).
 *
 * The Arena is a tab, so it stays mounted between visits: reading the param only in a `useState`
 * initializer honoured it once per session, and "Find a rival" then showed the player's own
 * challenges. This hook applies the param on every arrival and clears it afterwards, so a second
 * "Find a rival" (same param value) still switches, and a later plain tab press shows the player's
 * own challenges again. `useLocalSearchParams` hides `undefined` values, so the cleared key reads
 * as absent.
 */

import { useEffect, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import type { ArenaViewMode } from '../../../components/challenges/ArenaViewToggle';
import { ARENA_VIEW_OPEN, ARENA_VIEW_PARAM } from '../constants/challenges.constants';

type ArenaViewParams = { [ARENA_VIEW_PARAM]?: string };

export function useArenaViewParam(): [ArenaViewMode, (mode: ArenaViewMode) => void] {
  const router = useRouter();
  const { [ARENA_VIEW_PARAM]: viewParam } = useLocalSearchParams<ArenaViewParams>();
  // Seed from the param so a fresh mount shows the board without a one-frame flash of "mine".
  const [viewMode, setViewMode] = useState<ArenaViewMode>(viewParam === ARENA_VIEW_OPEN ? 'open' : 'mine');

  useEffect(() => {
    if (viewParam !== ARENA_VIEW_OPEN) return;
    setViewMode('open');
    router.setParams({ [ARENA_VIEW_PARAM]: undefined });
  }, [viewParam, router]);

  return [viewMode, setViewMode];
}
